import { debounce } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type UseAutocompleteSearchOptions<T> = {
  onSearch?: (query: string) => Promise<T[]>;
  /** Next-page fetch for the current query; results are appended. */
  onSearchMore?: () => Promise<T[]>;
  /** Whether more pages exist for the current query. */
  hasMore?: boolean;
  debounceMs?: number;
  minChars?: number;
  searchOnOpen?: boolean;
  onSearchError?: (error: unknown) => void;
};

export type UseAutocompleteSearchResult<T> = {
  isAsync: boolean;
  asyncOptions: T[];
  internalLoading: boolean;
  loadingMore: boolean;
  belowMinChars: boolean;
  runSearch: (query: string) => void;
  loadMore: () => void;
  handleOpen: () => void;
  reset: () => void;
};

/**
 * Debounced async search with request sequencing so stale responses are ignored.
 */
export const useAutocompleteSearch = <T>({
  onSearch,
  onSearchMore,
  hasMore = false,
  debounceMs = 300,
  minChars = 0,
  searchOnOpen = true,
  onSearchError,
}: UseAutocompleteSearchOptions<T>): UseAutocompleteSearchResult<T> => {
  const isAsync = typeof onSearch === 'function';
  const [asyncOptions, setAsyncOptions] = useState<T[]>([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Async + minChars: show type-to-search guidance before any query (incl. empty open)
  const [belowMinChars, setBelowMinChars] = useState(minChars > 0);
  const requestIdRef = useRef(0);
  const onSearchRef = useRef(onSearch);
  const onSearchMoreRef = useRef(onSearchMore);
  const hasMoreRef = useRef(hasMore);
  const onSearchErrorRef = useRef(onSearchError);

  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  useEffect(() => {
    onSearchMoreRef.current = onSearchMore;
  }, [onSearchMore]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    onSearchErrorRef.current = onSearchError;
  }, [onSearchError]);

  const executeSearch = useCallback(
    async (query: string) => {
      const searchFn = onSearchRef.current;
      if (!searchFn) return;

      if (query.length < minChars) {
        requestIdRef.current += 1;
        setBelowMinChars(minChars > 0);
        setAsyncOptions([]);
        setInternalLoading(false);
        return;
      }

      setBelowMinChars(false);
      const requestId = ++requestIdRef.current;
      // Clear previous suggestions so the list only shows loading for the new query
      setAsyncOptions([]);
      setInternalLoading(true);

      try {
        const results = await searchFn(query);
        if (requestId !== requestIdRef.current) return;
        setAsyncOptions(Array.isArray(results) ? results : []);
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        setAsyncOptions([]);
        onSearchErrorRef.current?.(error);
      } finally {
        if (requestId === requestIdRef.current) {
          setInternalLoading(false);
        }
      }
    },
    [minChars],
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        void executeSearch(query);
      }, debounceMs),
    [debounceMs, executeSearch],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const runSearch = useCallback(
    (query: string) => {
      if (!isAsync) return;
      if (query.length < minChars) {
        debouncedSearch.cancel();
        void executeSearch(query);
        return;
      }
      // Drop stale results as soon as the query changes (before debounce fires)
      setAsyncOptions([]);
      setInternalLoading(true);
      debouncedSearch(query);
    },
    [isAsync, minChars, debouncedSearch, executeSearch],
  );

  // Appends the next page for the current query. Shares the request sequence
  // with fresh searches so typing mid-fetch drops the stale append, and never
  // clears the list (unlike a fresh search) so loaded pages stay visible.
  // Scroll events fire faster than fetches, so an in-flight flag (not state)
  // guards against fetching the same page twice.
  const loadingMoreRef = useRef(false);
  const loadMore = useCallback(() => {
    if (!isAsync || loadingMoreRef.current) return;
    const moreFn = onSearchMoreRef.current;
    if (!moreFn || !hasMoreRef.current) return;
    loadingMoreRef.current = true;
    const requestId = ++requestIdRef.current;
    setLoadingMore(true);
    void (async () => {
      try {
        const items = await moreFn();
        if (requestId !== requestIdRef.current) return;
        if (Array.isArray(items) && items.length > 0) {
          setAsyncOptions((prev) => [...prev, ...items]);
        }
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        onSearchErrorRef.current?.(error);
      } finally {
        // Always clear: a stale append (superseded by a fresh search, which
        // bumped the sequence) must not leave the spinner stuck on.
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    })();
  }, [isAsync]);

  const handleOpen = useCallback(() => {
    if (!isAsync || !searchOnOpen) return;
    void executeSearch('');
  }, [isAsync, searchOnOpen, executeSearch]);

  const reset = useCallback(() => {
    requestIdRef.current += 1;
    debouncedSearch.cancel();
    setAsyncOptions([]);
    setInternalLoading(false);
    setLoadingMore(false);
    setBelowMinChars(minChars > 0);
  }, [debouncedSearch, minChars]);

  return {
    isAsync,
    asyncOptions,
    internalLoading,
    loadingMore,
    belowMinChars,
    runSearch,
    loadMore,
    handleOpen,
    reset,
  };
};
